import { useQuery } from 'react-query';
import { useConfig } from '../components/ConfigContext';
import { compareType, getQueryStatus, load } from '../lib';

const getQuery = (filters) => {
  const { seasonId, officeId, divisionId, type } = filters;

  const query = {
    seasonId,
    type,
    'category.divisionId': divisionId,
    officeId,
  }

  return query;
}

const getInitialPage = (pastCount, totalCount, perPage) => {
  if (pastCount == null || totalCount == null) return;

  const initialPage = pastCount ? Math.ceil(pastCount / perPage) : 1
  const lastPage = Math.ceil(totalCount / perPage) || 1;
  return initialPage <= lastPage ? initialPage : lastPage;
}

const loadTotal = (filters, apiUrl) => {
  const request = load(`${apiUrl}/schedules/count`, {
    where: getQuery(filters)
  })
  const result = request.then(result => result.count);

  result.cancel = request.cancel;
  return result;
}

const loadInitialPage = (filters, apiUrl) => {
  const dateFilter = {
    startDate: { lte: new Date() },
  }

  const { officeId, ...scheduleFilter } = getQuery(filters)
  const whereFilter = {
    officeId
  }

  if (Object.keys(scheduleFilter).length) {
    whereFilter.and = [ scheduleFilter, dateFilter ]
  } else {
    Object.assign(whereFilter, dateFilter)
  }

  const request = load(`${apiUrl}/schedules/count`, {
    where: whereFilter,
  })
  const result = request.then(result => result.count + 1)

  result.cancel = request.cancel;
  return result;
}

const loadSchedule = (scheduleId, apiUrl) => {
  return load(`${apiUrl}/schedules/${scheduleId}`, {
    filter: {
      include: { 'category': true, 'office': true }
    }
  })
}

const loadSchedules = (filters, currentPage, perPage, apiUrl) => {
  let limit = perPage;
  if (limit < 0) {
    limit = 0;
  }

  let skip;
  if (currentPage && perPage) {
    skip = (currentPage - 1) * perPage;
    if (skip < 0) {
      skip = 0;
    }
  }

  const request = load(`${apiUrl}/schedules`, {
    filter: {
      where: getQuery(filters),
      order: ['startDate ASC', 'category.order ASC', 'name ASC'],
      include: 'category',
      limit,
      skip,
    }
  })

  const result = request
    .then(results => results.sort(compareType))

  result.cancel = request.cancel;
  return result;
}

const loadTeamSchedules = (teamId, apiUrl) => {
  const request = load(`${apiUrl}/scheduleteams`, {
    filter: {
      where: { teamId },
      include: { 'schedule': 'category', 'group': true },
    }
  })
  const result = request
    .then(results => results.filter(result => result.schedule))
    .then(results => results.sort((a, b) => compareType(a.schedule, b.schedule)))

  result.cancel = request.cancel;
  return result;
}

export const useSchedules = (filter, { currentPage, perPage, ...options } = {}) => {
  const { apiUrl } = useConfig();

  return useQuery(
    ["schedules", filter, currentPage, perPage],
    () => loadSchedules(filter, currentPage, perPage, apiUrl),
    options
  )
}

export const useSchedulesList = (filters, { currentPage, perPage, totalOptions, initialOptions, ...options }) => {
  const { apiUrl } = useConfig();

  const { data: total, status: totalStatus } = useQuery(
    ['schedules-list-total', filters],
    () => loadTotal(filters, apiUrl),
    totalOptions
  )

  const { data: pastCount, status: initialStatus } = useQuery(
    ['schedules-list-initial', filters],
    () => loadInitialPage(filters, apiUrl),
    initialOptions
  )

  const { status: dataStatus, ...data } = useSchedules(filters, {
    currentPage,
    perPage,
    enabled: currentPage != null,
    ...options
  })

  const initialPage = getInitialPage(pastCount, total, perPage);
  const status = getQueryStatus(totalStatus, initialStatus, dataStatus);
  return { initialPage, total, status, ...data }
}

export const useTeamSchedules = (teamId, options) => {
  const { apiUrl } = useConfig();

  return useQuery(
    ['schedules-team-data', teamId],
    () => loadTeamSchedules(teamId, apiUrl),
    { enabled: !!teamId, ...options }
  )
}

export const useSchedule = (scheduleId, options) => {
  const { apiUrl } = useConfig();

  return useQuery(
    ['schedule', scheduleId],
    () => loadSchedule(scheduleId, apiUrl),
    { enabled: !!scheduleId, ...options }
  )
}
